// app/javascript/controllers/dae20/widget_loader_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    url: String,             // URL для загрузки JS-кода виджета
    config: { type: String, default: '{}' },
    scope: String,           // 'page' | 'component'
    pageId: String,
    pageCode: String,
    componentId: String,
    componentName: String,
    componentType: String,   // template_code: form, table, etc.
    workspaceId: String
  }

  static targets = ["container"]

  connect() {
    // Ждём turbo:load, чтобы все компоненты были отрендерены
    if (document.readyState === 'complete') {
      // Небольшая задержка чтобы компоненты успели инициализироваться
      requestAnimationFrame(() => this._loadWidget())
    } else {
      this._onReady = () => {
        requestAnimationFrame(() => this._loadWidget())
      }
      document.addEventListener('turbo:load', this._onReady, { once: true })
    }
  }

  disconnect() {
    if (this._onReady) {
      document.removeEventListener('turbo:load', this._onReady)
    }
    // Очищаем контейнер виджета
    if (this.hasContainerTarget) {
      this.containerTarget.innerHTML = ''
    }
    // Отзываем blob URL если создавали
    if (this._blobUrl) {
      URL.revokeObjectURL(this._blobUrl)
      this._blobUrl = null
    }
  }

  async _loadWidget() {
    if (!this.urlValue) return
    if (!this.hasContainerTarget) return

    const sdk = this._buildSdk()

    try {
      // Загружаем JS-контент виджета с сервера
      const response = await fetch(this.urlValue, {
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const jsCode = await response.text()

      // Создаём blob URL для dynamic import (ES module)
      const blob = new Blob([jsCode], { type: 'application/javascript' })
      this._blobUrl = URL.createObjectURL(blob)

      const module = await import(this._blobUrl)

      if (typeof module.default === 'function') {
        await module.default(this.containerTarget, sdk)
      } else if (typeof module.init === 'function') {
        await module.init(this.containerTarget, sdk)
      }
    } catch (err) {
      console.error(`[dae20-widget-loader] Failed to load widget: ${this.urlValue}`, err)
      this.containerTarget.innerHTML =
        '<div class="text-xs text-red-500 p-1">Widget load error</div>'
    }
  }

  _buildSdk() {
    let config = {}
    try { config = JSON.parse(this.configValue) } catch (_) { /* ignore */ }

    return {
      page: {
        id: this.pageIdValue,
        code: this.pageCodeValue
      },
      component: {
        id: this.componentIdValue,
        name: this.componentNameValue,
        type: this.componentTypeValue
      },
      workspace: {
        id: this.workspaceIdValue
      },
      config,
      // Helper для загрузки данных из API виджетов
      fetchData: async (endpoint) => {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        const resp = await fetch(endpoint, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken || ''
          },
          credentials: 'same-origin'
        })
        if (!resp.ok) throw new Error(`Widget data fetch failed: ${resp.status}`)
        return resp.json()
      }
    }
  }
}
